const controllers = new WeakMap();
const portfolioCatalog = JSON.parse(document.getElementById('GmPortfolioCatalog')?.textContent || '{"categories":{},"projects":{}}');
const projectCache = new Map();
const cacheLimit = 12;
const prefetchRequests = new Map();
const mobileProjectLayout = matchMedia('(max-width:899px)');
const leadPhotos = new WeakMap();

function updateProjectPhotoOrder(scope) {
  const selector = '.gm-project__layout, .gm-bespoke__dialog-content';
  const layouts = [...scope.querySelectorAll(selector)];
  if (scope.matches?.(selector)) layouts.unshift(scope);
  for (const layout of layouts) {
    const media = layout.querySelector(':scope > .gm-bespoke__story-media');
    const copy = layout.querySelector(':scope > .gm-bespoke__story-copy');
    const title = copy?.querySelector('[data-gm-story-title], h1, h2');
    if (!media || !title) continue;
    let lead = leadPhotos.get(layout);
    if (lead && !layout.contains(lead.figure)) {
      leadPhotos.delete(layout);
      lead = null;
    }
    if (mobileProjectLayout.matches) {
      if (!lead) {
        const figure = [...media.querySelectorAll('figure')].find((item) => item.querySelector('img'));
        if (!figure) continue;
        lead = { figure, parent: figure.parentNode, next: figure.nextSibling, media, hidden: media.hidden };
        leadPhotos.set(layout, lead);
      }
      title.after(lead.figure);
      lead.figure.classList.add('gm-project__lead-photo');
      media.hidden = !media.querySelector('figure, img, video');
    } else if (lead) {
      lead.parent.insertBefore(lead.figure, lead.next?.parentNode === lead.parent ? lead.next : null);
      lead.figure.classList.remove('gm-project__lead-photo');
      lead.media.hidden = lead.hidden;
      leadPhotos.delete(layout);
    }
  }
}

mobileProjectLayout.addEventListener('change', () => updateProjectPhotoOrder(document));

function themeUrl(path) {
  const url = new URL(path, window.location.href);
  if (url.origin !== window.location.origin) throw new Error('Invalid project URL');
  const theme = window.Shopify?.theme;
  if (theme?.id && theme.role !== 'main') url.searchParams.set('preview_theme_id', theme.id);
  return url;
}

function sectionUrl(path, sectionId) {
  const url = themeUrl(path);
  url.searchParams.set('section_id', sectionId);
  return url;
}

async function fetchSection(url, selector, signal) {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
  const section = parsed.querySelector(selector);
  if (!section) throw new Error('Section content is missing');
  return section;
}

function rememberProject(key, section) {
  projectCache.delete(key);
  projectCache.set(key, section);
  if (projectCache.size > cacheLimit) projectCache.delete(projectCache.keys().next().value);
}

async function prepareProject(section) {
  const source = section.querySelector('.gm-bespoke__story-media img');
  if (source) {
    const image = new Image();
    image.src = source.src;
    await image.decode().catch(() => {});
  }
  return section;
}

function prefetchProject(path) {
  const url = sectionUrl(path, 'gemmarii-project-dialog');
  if (projectCache.has(url.href) || prefetchRequests.has(url.href) || prefetchRequests.size >= 2) return;
  const request = fetchSection(url, '[data-gm-dialog-content]').then(prepareProject).then((section) => {
    rememberProject(url.href, section);
    return section;
  });
  prefetchRequests.set(url.href, request);
  void request.catch(() => {}).finally(() => prefetchRequests.delete(url.href));
}

function projectDialogContent(section, projectUrl) {
  const media = section.querySelector('.gm-project__layout .gm-bespoke__story-media');
  const copy = section.querySelector('.gm-project__layout .gm-bespoke__story-copy');
  if (!media || !copy) throw new Error('Project media or story is missing');
  const content = document.createDocumentFragment();
  const story = copy.cloneNode(true);
  const title = story.querySelector('h1');
  if (title) {
    const heading = document.createElement('h2');
    for (const attribute of title.attributes) heading.setAttribute(attribute.name, attribute.value);
    heading.setAttribute('data-gm-story-title', '');
    heading.replaceChildren(...title.childNodes);
    title.replaceWith(heading);
  }
  story.querySelector('.gm-project__navigation')?.remove();
  populateProjectActions(story.querySelector('.gm-project__inquiry'), projectUrl, true);
  content.append(media.cloneNode(true), story);
  return content;
}

class PortfolioController {
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('dialog');
    this.content = this.dialog?.querySelector('.gm-bespoke__dialog-content');
    this.grid = root.querySelector('[data-gm-grid]');
    this.pagination = root.querySelector('[data-gm-pagination]');
    this.status = root.querySelector('[data-gm-pagination-status]');
    this.moreButton = this.pagination?.querySelector('[data-gm-load-more]');
    this.sentinel = root.querySelector('[data-gm-sentinel]');
    this.lifecycle = new AbortController();
    if (root.hasAttribute('data-gm-filterable') && this.grid) {
      this.cards = [...this.grid.querySelectorAll(':scope > .gm-bespoke__work')];
      this.categoryMap = Object.fromEntries(Object.entries(portfolioCatalog.projects).map(([key, record]) => [key, record.categories]));
      this.filterButtons = [...root.querySelectorAll('[data-gm-filter]')];
      this.showAll = root.dataset.gmShowAll === 'true';
      this.resultsHeading = root.querySelector('[data-gm-results-heading]');
      this.allHeading = this.resultsHeading.textContent;
      this.setFilter('all');
      root.setAttribute('data-gm-ready', '');
    }
    root.addEventListener('click', (event) => this.onClick(event), { signal: this.lifecycle.signal });
    for (const name of ['pointerover', 'focusin', 'pointerdown']) {
      root.addEventListener(name, (event) => {
        if (!(event.target instanceof Element)) return;
        const trigger = event.target.closest('[data-gm-project-url]');
        if (trigger) prefetchProject(trigger.dataset.gmProjectUrl);
      }, { signal: this.lifecycle.signal });
    }
    this.dialog?.addEventListener('close', () => this.onClose(), { signal: this.lifecycle.signal });
    if (this.moreButton && this.sentinel && 'IntersectionObserver' in window) {
      this.moreButton.hidden = true;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void this.loadMore(this.moreButton);
      }, { rootMargin: '500px 0px' });
      this.observer.observe(this.sentinel);
    }
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-gm-close]')) {
      this.dialog?.close();
      return;
    }
    if (event.target === this.dialog) {
      const { left, right, top, bottom } = this.dialog.getBoundingClientRect();
      if (event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom) {
        this.dialog.close();
      }
      return;
    }
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const filter = event.target.closest('[data-gm-filter]');
    if (filter && this.cards) {
      this.setFilter(filter.dataset.gmFilter);
      return;
    }
    const more = event.target.closest('[data-gm-load-more]');
    if (more) {
      event.preventDefault();
      void this.loadMore(more);
      return;
    }
    const trigger = event.target.closest('[data-gm-open]');
    if (!trigger || !this.dialog?.showModal || !this.content) return;
    const templateId = trigger.dataset.gmOpen;
    const template = templateId ? this.root.querySelector(`#${CSS.escape(templateId)}`) : null;
    if (!trigger.dataset.gmProjectUrl && !template) return;
    event.preventDefault();
    void this.openProject(trigger, template);
  }

  showStatus(text, title) {
    const message = document.createElement('div');
    message.className = 'gm-bespoke__dialog-status';
    const heading = document.createElement('h2');
    heading.id = this.dialog.getAttribute('aria-labelledby');
    heading.textContent = title;
    const paragraph = document.createElement('p');
    paragraph.setAttribute('role', 'status');
    paragraph.textContent = text;
    message.append(heading, paragraph);
    this.content.replaceChildren(message);
  }

  async openProject(trigger, template) {
    this.request?.abort();
    this.request = new AbortController();
    const signal = this.request.signal;
    this.opener = trigger;
    const title = trigger.getAttribute('aria-label') || trigger.textContent.trim();
    trigger.setAttribute('aria-busy', 'true');
    try {
      let section = template?.content || template;
      const path = trigger.dataset.gmProjectUrl;
      if (path) {
        const url = sectionUrl(path, 'gemmarii-project-dialog');
        section = projectCache.get(url.href);
        if (!section) {
          section = await (prefetchRequests.get(url.href) || fetchSection(url, '[data-gm-dialog-content]', signal).then(prepareProject));
          rememberProject(url.href, section);
        }
      }
      if (signal.aborted) return;
      const nodes = path
        ? projectDialogContent(section, trigger.dataset.gmProjectUrl)
        : section.cloneNode(true);
      this.content.replaceChildren(...nodes.childNodes);
      updateProjectPhotoOrder(this.content);
      initializeProjectVideos(this.content);
      const heading = this.content.querySelector('[data-gm-story-title]');
      if (heading) heading.id = this.dialog.getAttribute('aria-labelledby');
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.dialog.querySelector('[data-gm-close]')?.focus();
    } catch (error) {
      if (signal.aborted) return;
      this.showStatus(this.root.dataset.gmLoadError, title);
      if (!this.dialog.open) this.dialog.showModal();
      if (trigger.href) {
        const link = document.createElement('a');
        link.href = themeUrl(trigger.href).href;
        link.textContent = title;
        this.content.firstElementChild.append(link);
      }
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  }

  onClose() {
    if (this.deepLinked) {
      const url = new URL(window.location.href);
      url.searchParams.delete('gm_project');
      history.replaceState(history.state, '', url.href);
      this.deepLinked = false;
    }
    this.request?.abort();
    this.content?.querySelectorAll('video').forEach((video) => video.pause());
    this.content?.replaceChildren();
    this.content?.removeAttribute('aria-busy');
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  }

  setFilter(category, visibleLimit) {
    this.activeFilter = category;
    this.filteredCards = this.cards.filter(card => category === 'all' || this.categoryMap[card.dataset.gmProject]?.includes(category));
    this.visibleLimit = visibleLimit ?? (this.showAll ? this.filteredCards.length : 25);
    for (const button of this.filterButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.gmFilter === category));
    }
    const button = this.filterButtons.find(button => button.dataset.gmFilter === category);
    this.resultsHeading.textContent = category === 'all' ? this.allHeading : button.textContent.trim();
    this.renderCards();
  }

  renderCards() {
    const visible = new Set(this.filteredCards.slice(0, this.visibleLimit));
    for (const card of this.cards) card.hidden = !visible.has(card);
    const hasMore = this.visibleLimit < this.filteredCards.length;
    if (this.pagination) this.pagination.hidden = !hasMore;
    if (this.status) this.status.textContent = this.filteredCards.length + ' ' + this.root.dataset.gmResultsLabel;
    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
      if (hasMore && !this.restoring) this.observer.observe(this.sentinel);
    }
  }

  async loadMore() {
    if (!this.filteredCards || this.visibleLimit >= this.filteredCards.length) return;
    this.visibleLimit += 25;
    this.renderCards();
  }

  destroy() {
    this.observer?.disconnect();
    this.lifecycle.abort();
    this.request?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.onClose();
  }
}

function mergePortfolioGrids(scope) {
  const roots = [...document.querySelectorAll('.gm-bespoke')];
  let target = null;
  let previousSection = null;
  for (const root of roots) {
    const section = root.closest('.shopify-section');
    const grid = root.querySelector('.gm-bespoke__portfolio > .gm-bespoke__photo-grid');
    const follows = section && previousSection && section.previousElementSibling === previousSection;
    if (root.classList.contains('gm-bespoke--continuation') && follows && target && grid) {
      target.append(...grid.children);
      root.querySelector('.gm-bespoke__portfolio').hidden = true;
    } else {
      target = grid;
    }
    if (!root.classList.contains('gm-bespoke--continues')) target = null;
    previousSection = section;
  }
}

const projectVideoPlayers = new WeakSet();

function initializeProjectVideos(scope) {
  const knownVideoPreviews = [{"hash":"f3b65ffa0c86442ab085c4a953c8468a","poster":"https://cdn.shopify.com/s/files/1/1061/7507/0545/files/preview_images/f3b65ffa0c86442ab085c4a953c8468a.thumbnail.0000000000.jpg?v=1784495573","width":1916,"height":984},{"hash":"3de6f7104e5b4f93adcb6fdbb6840474","poster":"https://cdn.shopify.com/s/files/1/1061/7507/0545/files/saet-rosaguld-3d-video-forste-billede.webp?v=1791910684","width":640,"height":390},{"hash":"4d8f302b654e49ada354f5bb9661e7e9","poster":"https://cdn.shopify.com/s/files/1/1061/7507/0545/files/preview_images/4d8f302b654e49ada354f5bb9661e7e9.thumbnail.0000000000.jpg?v=1791119161","width":360,"height":640},{"hash":"207e76fea4b64346aed44dccc78eee5b","poster":"https://cdn.shopify.com/s/files/1/1061/7507/0545/files/preview_images/207e76fea4b64346aed44dccc78eee5b.thumbnail.0000000000.jpg?v=1791121791","width":720,"height":1280}];
  for (const video of scope.querySelectorAll('.gm-bespoke__story-media video')) {
    const preview = knownVideoPreviews.find(item => (video.currentSrc || video.getAttribute('src') || video.querySelector('source')?.getAttribute('src') || '').includes(item.hash));
    if (preview && !video.getAttribute('poster')) video.poster = preview.poster;
    video.preload = 'none';
    video.playsInline = true;
    if (!video.getAttribute('poster') || video.closest('[data-gm-video-player]')) continue;
    const width = preview?.width || Number(video.getAttribute('width')) || 16;
    const height = preview?.height || Number(video.getAttribute('height')) || 9;
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-gm-video-player', '');
    wrapper.style.cssText = 'position:relative;width:100%;aspect-ratio:' + width + '/' + height;
    video.before(wrapper);
    wrapper.append(video);
    video.style.cssText += ';display:block;width:100%;height:100%;object-fit:contain';
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('data-gm-video-play', '');
    button.setAttribute('aria-label', 'Afspil videoen');
    button.style.cssText = 'display:none;position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:72px;height:72px;border:0;border-radius:50%;background:#fff;place-items:center;cursor:pointer;box-shadow:0 2px 16px #0004';
    const triangle = document.createElement('span');
    triangle.setAttribute('aria-hidden', 'true');
    triangle.style.cssText = 'width:0;height:0;border-top:13px solid transparent;border-bottom:13px solid transparent;border-left:20px solid #222;margin-left:5px';
    button.append(triangle);
    wrapper.append(button);
  }
  const players = [...scope.querySelectorAll('[data-gm-video-player]')];
  if (scope.matches?.('[data-gm-video-player]')) players.unshift(scope);
  for (const player of players) {
    if (projectVideoPlayers.has(player)) continue;
    const video = player.querySelector('video');
    const button = player.querySelector('[data-gm-video-play]');
    if (!video || !button) continue;
    projectVideoPlayers.add(player);
    video.controls = false;
    button.style.cssText += ';width:96px;height:96px;border:10px solid rgba(255,255,255,.9);border-radius:50%;background:rgba(0,0,0,.1);color:#fff;padding:14px;';
    button.style.display = 'grid';
    video.addEventListener('play', () => { button.style.display = 'none'; });
    video.addEventListener('ended', () => {
      video.controls = false;
      button.style.display = 'grid';
    });
    button.addEventListener('click', async () => {
      video.controls = true;
      try {
        await video.play();
        button.style.display = 'none';
      } catch {
        button.style.display = 'none';
        video.controls = true;
      }
    });
  }
}

function populateProjectActions(inquiry, projectUrl, portfolioCard = false) {
  if (!inquiry || inquiry.hasAttribute('data-gm-project-actions')) return;
  const primary = inquiry.querySelector('a');
  if (!primary) return;
  // Older project content may contain the general design-page URL.
  // Normalize both standalone stories and portfolio dialogs to the actual form.
  primary.href = '/pages/contact#gemmarii-contact-form';
  const path = new URL(projectUrl, window.location.href).pathname;
  const record = Object.values(portfolioCatalog.projects).find(record => record.path === path);
  const shop = portfolioCatalog.categories[record?.shop || 'all'];
  inquiry.setAttribute('data-gm-project-actions', '');
  inquiry.replaceChildren(primary);
  for (const action of [
    { url: '/pages/priser-paa-speciallavede-smykker', label: 'Hvad påvirker prisen?' },
    shop
  ]) {
    if (!action) continue;
    const link = document.createElement('a');
    link.className = primary.className;
    link.href = action.url;
    link.textContent = action.label;
    inquiry.append(link);
  }
}

function initializeProjectActions(scope) {
  const roots = [...scope.querySelectorAll('[data-gm-standalone]')];
  if (scope.matches?.('[data-gm-standalone]')) roots.unshift(scope);
  for (const root of roots) {
    const inquiry = root.querySelector('.gm-project__inquiry');
    populateProjectActions(inquiry, window.location.href);
    for (const portfolioLink of root.querySelectorAll('.gm-project__navigation a[href*="view=gem-portfolio"], .gm-bespoke__crumb a[href*="view=gem-portfolio"]')) portfolioLink.textContent = 'Se portfolio';
  }
}

function initialize(scope) {
  mergePortfolioGrids(scope);
  initializeProjectActions(scope);
  updateProjectPhotoOrder(scope);
  initializeProjectVideos(scope);
  const roots = [...scope.querySelectorAll('.gm-bespoke')];
  if (scope.matches?.('.gm-bespoke')) roots.unshift(scope);
  for (const root of roots) {
    if (!controllers.has(root)) controllers.set(root, new PortfolioController(root));
  }
}

function openLinkedPortfolioProject() {
  const key = new URL(window.location.href).searchParams.get('gm_project');
  if (!key) return;
  const root = document.querySelector('[data-gm-filterable]');
  const controller = root && controllers.get(root);
  if (!controller?.cards) return;
  const index = controller.cards.findIndex(card => card.dataset.gmProject === key);
  if (index < 0) return;
  const trigger = controller.cards[index].querySelector('[data-gm-open][data-gm-project-url]');
  if (!trigger) return;
  controller.setFilter('all', Math.max(25, Math.ceil((index + 1) / 25) * 25));
  controller.deepLinked = true;
  void controller.openProject(trigger);
}
initialize(document);
openLinkedPortfolioProject();

document.addEventListener('gm:navigation:capture', event => {
  event.detail.portfolio = [...document.querySelectorAll('[data-gm-grid]')].map(grid => {
    const controller = controllers.get(grid.closest('.gm-bespoke'));
    return { count: controller?.visibleLimit || grid.children.length, filter: controller?.activeFilter || 'all' };
  });
});
document.addEventListener('gm:navigation:restore', event => {
  if (!Array.isArray(event.detail)) return;
  [...document.querySelectorAll('[data-gm-grid]')].forEach((grid, index) => {
    const controller = controllers.get(grid.closest('.gm-bespoke'));
    if (!controller?.cards || !event.detail[index]) return;
    controller.restoring = true;
    controller.observer?.disconnect();
    if (controller.dialog?.open) controller.dialog.close();
    const saved = event.detail[index];
    const filter = controller.filterButtons.some(button => button.dataset.gmFilter === saved.filter) ? saved.filter : 'all';
    controller.setFilter(filter, Math.min(Number(saved.count) || 25, 400));
    controller.restoring = false;
    if (controller.sentinel && controller.pagination && !controller.pagination.hidden) controller.observer?.observe(controller.sentinel);
  });
});
document.dispatchEvent(new CustomEvent('gm:navigation:ready'));
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  for (const root of event.target.querySelectorAll('.gm-bespoke')) {
    controllers.get(root)?.destroy();
    controllers.delete(root);
  }
});

